import React, { useState, useEffect } from 'react';
import '../styles/TeamView.css';
import { motion, AnimatePresence } from 'framer-motion';
import { UserPlus, User, X, Mail, Lock, Loader2, Trophy, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { io } from 'socket.io-client';

// Connect to your backend socket
const socket = io('http://localhost:5000');

const TeamView = ({ user, showToast }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newEngineer, setNewEngineer] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [onlineIds, setOnlineIds] = useState([]); // Real-time online IDs
  const [fetching, setFetching] = useState(true);

    // TeamView.js
    // TeamView.js
    useEffect(() => {
        fetchEngineers();

        // 1. Listen for updates
        const handleUpdate = (ids) => {
            console.log("TeamView Syncing IDs:", ids);
            setOnlineIds(ids);
        };

        socket.on('updateUserStatus', handleUpdate);

        // 2. CRITICAL: Ask the server for the list RIGHT NOW 
        // This handles the "Refresh" case where we might have missed the broadcast
        socket.emit('requestUserStatus');

        return () => {
            socket.off('updateUserStatus', handleUpdate);
        };
    }, []);

    useEffect(() => {
      socket.on('updateUserStatus', (ids) => {
        console.log("TeamView received IDs:", ids); // If your ID is in this list, you should be green
        setOnlineIds(ids);
      });
      return () => socket.off('updateUserStatus');
    }, []);

    const fetchEngineers = async () => {
    try {
      setFetching(true);
      const res = await axios.get('http://localhost:5000/api/admin/engineers');
      console.log("Team Data received:", res.data); // <--- ADD THIS
      if (res.data.success) {
        setMembers(res.data.data || []);
      } else {
        setMembers(res.data || []); // Fallback if structure is different
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      showToast("Could not load team members", "error");
    } finally {
      setFetching(false);
    }
  };

  const handleCreateEngineer = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post('http://localhost:5000/api/admin/create-engineer', newEngineer);
      if (res.data.success) {
        showToast("Engineer account created!", "success");
        setShowCreateModal(false);
        setNewEngineer({ name: '', email: '', password: '' });
        fetchEngineers();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Error", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="team-container">
      <header className="top-header">
        <div>
          <h2>TEAM PERFORMANCE</h2>
          <p style={{ color: '#64748b', fontSize: '14px' }}>Monitor live activity and agent productivity</p>
        </div>
        <button className="new-ticket-btn" onClick={() => setShowCreateModal(true)} style={{ background: '#6366f1' }}>
          <UserPlus size={18} /> Create Engineer
        </button>
      </header>

      <div className="team-layout-grid">
        <section className="members-section">
          {fetching ? (
            <div className="loading-state"><Loader2 className="spinner" /></div>
          ) : (
            <div className="members-grid">
              {members.map((m) => {
                const isMe = m.id === user?.id;
                // Check if the member ID exists in our live onlineIds array
                const isActive = onlineIds.some(onlineId => String(onlineId) === String(m.id));

                return (
                  <motion.div key={m.id} whileHover={{ y: -5 }} className={`member-card modern-card ${isMe ? 'is-me' : ''}`}>
                    <div className="member-card-header">
                      <div className="member-avatar">
  {m.name.charAt(0)}
  {/* If isActive is true, it gets 'online-pulse' (Green), else 'offline' (Gray) */}
  <span className={`status-dot ${isActive ? 'online-pulse' : 'offline'}`}></span>
                      </div>

                      <div className="member-info">
                        <h4>{m.name}</h4>
                        {/* Changing the text color based on status */}
                        <span style={{ 
                          fontSize: '10px', 
                          color: isActive ? '#10b981' : '#94a3b8', 
                          fontWeight: '700' 
                        }}>
                          {isActive ? '● ONLINE' : '○ OFFLINE'}
                        </span>
                      </div>
                    </div>

                    <div className="member-stats-row">
                      <div className="stat"><span>Open</span><p style={{color: '#f59e0b'}}>{m.openTickets || 0}</p></div>
                      <div className="stat"><span>Resolved</span><p style={{color: '#10b981'}}>{m.resolvedTickets || 0}</p></div>
                      <div className="stat"><span>Total</span><p>{m.totalTickets || 0}</p></div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </section>

        <aside className="leaderboard-card">
          <div className="leaderboard-header">
            <Trophy size={20} color="#f59e0b" />
            <h3>Top Performers</h3>
          </div>
          <div className="leaderboard-list">
            {[...members].sort((a,b) => b.resolvedTickets - a.resolvedTickets).slice(0, 5).map((m, index) => (
              <div key={m.id} className={`leader-item ${m.id === user?.id ? 'leader-me' : ''}`}>
                <span className="rank">#{index + 1}</span>
                <span className="name text-truncate">{m.name}</span>
                <span className="score">{m.resolvedTickets || 0} <small>done</small></span>
              </div>
            ))}
          </div>
        </aside>
      </div>

      {/* Create Modal stays the same logic-wise */}
      <AnimatePresence>
        {showCreateModal && (
          <div className="modal-overlay1">
            <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="modal-content admin-modal">
              <div className="modal-header1">
                <div className="header-icon"><CheckCircle2 size={20} color="#6366f1" /></div>
                <h3>New Staff Account</h3>
                <button className="close-btn" onClick={() => setShowCreateModal(false)}><X size={20} /></button>
              </div>
              <form onSubmit={handleCreateEngineer} className="modal-form modern-form">
                 <div className="input-field">
                  <label><User size={16} /> Full Name</label>
                  <input type="text" placeholder="John Doe" required value={newEngineer.name} onChange={e => setNewEngineer({...newEngineer, name: e.target.value})} />
                </div>
                <div className="input-field">
                  <label><Mail size={16} /> Email Address</label>
                  <input type="email" placeholder="staff@company.com" required value={newEngineer.email} onChange={e => setNewEngineer({...newEngineer, email: e.target.value})} />
                </div>
                <div className="input-field">
                  <label><Lock size={16} /> Password</label>
                  <input type="password" placeholder="••••••••" required value={newEngineer.password} onChange={e => setNewEngineer({...newEngineer, password: e.target.value})} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={() => setShowCreateModal(false)}>Cancel</button>
                  <button type="submit" className="confirm-btn" disabled={loading}>{loading ? "Saving..." : "Create"}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default TeamView;